import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Paperclip, X } from 'lucide-react';
import {
  PRD_BRAINSTORM_MAX_ROUNDS,
  PRD_BRAINSTORM_MIN_DESCRIPTION,
  PRD_SOURCE_MAX_FILES,
  type Job,
  type PrdBrainstormQuestion,
  type PrdBrainstormResult,
  type PrdBrainstormRound,
  type PrdExtractionResult,
} from '@allemnii-pulse/shared';
import { useDocument } from '../../lib/firestore.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/utils.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { Textarea } from '../../components/ui/Textarea.js';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog.js';
import { SOURCE_FILE_ACCEPT, uploadErrorMessage, uploadSourceFile } from '../../lib/sourceUploads.js';

type OpenRound = { clarification?: string; questions: PrdBrainstormQuestion[] };
type SourceDoc = { name: string; storagePath: string };

/**
 * Drafts a PRD from a short project description and/or attached documents (uploaded as soon as
 * they're picked, then resent by path each round): the LLM asks a few rounds of follow-up
 * questions (POST .../prd/brainstorm, see apps/api/src/routes/llm.ts), then returns a draft that
 * `onDraft` merges into the PRD form — the same hand-off as uploading a document, so nothing is
 * saved until the user saves the draft. The conversation lives here, not in Firestore, and is
 * kept while the dialog is closed so an accidental close doesn't lose the answers.
 */
export function PrdBrainstormDialog({
  projectId,
  open,
  onOpenChange,
  formHasContent,
  onDraft,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether the PRD form already has content the draft would overwrite. */
  formHasContent: boolean;
  onDraft: (prd: PrdExtractionResult) => void;
}) {
  const [description, setDescription] = useState('');
  const [sources, setSources] = useState<SourceDoc[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rounds, setRounds] = useState<PrdBrainstormRound[]>([]);
  const [current, setCurrent] = useState<OpenRound | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [jobId, setJobId] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setDescription('');
    setSources([]);
    setRounds([]);
    setCurrent(null);
    setAnswers({});
    setJobId(null);
    setError(null);
  }

  const { data: job } = useDocument<Job>(jobId ? `jobs/${jobId}` : null);
  useEffect(() => {
    if (job?.status === 'succeeded' && job.result) {
      const result = job.result as unknown as PrdBrainstormResult;
      setJobId(null);
      if (result.status === 'ready' && result.prd) {
        onDraft(result.prd);
        reset();
        onOpenChange(false);
      } else {
        setCurrent({ clarification: result.clarification, questions: result.questions ?? [] });
        setAnswers({});
      }
    } else if (job?.status === 'failed') {
      setError(job.error ?? 'The brainstorm failed. Please try again.');
      setJobId(null);
    }
    // onDraft/onOpenChange are fresh closures each render; only a job update should fire this.
  }, [job]);

  async function handleAttach(files: File[]) {
    setError(null);
    const room = PRD_SOURCE_MAX_FILES - sources.length;
    if (files.length > room) {
      setError(`Attach up to ${PRD_SOURCE_MAX_FILES} documents.`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map(async (f) => ({ name: f.name, storagePath: await uploadSourceFile(projectId, 'prd', f) })),
      );
      setSources((s) => [...s, ...uploaded]);
    } catch (err) {
      setError(uploadErrorMessage(err, 'Could not upload the document. Please try again.'));
    } finally {
      setUploading(false);
    }
  }

  async function send(nextRounds: PrdBrainstormRound[], finish: boolean) {
    setSubmitting(true);
    setError(null);
    try {
      const { jobId: id } = await api.post<{ jobId: string }>(
        `/projects/${projectId}/prd/brainstorm`,
        {
          description: description.trim(),
          sourcePaths: sources.map((s) => s.storagePath),
          rounds: nextRounds,
          finish,
        },
      );
      setRounds(nextRounds);
      setCurrent(null);
      setFinishing(finish || nextRounds.length >= PRD_BRAINSTORM_MAX_ROUNDS);
      setJobId(id);
    } catch {
      setError('Could not reach the brainstorm service. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  /** Closes out the open round with whatever was answered — blank answers count as skipped. */
  function withCurrentRound(): PrdBrainstormRound[] {
    if (!current) return rounds;
    const answered = current.questions
      .map((q) => ({ questionId: q.id, answer: (answers[q.id] ?? '').trim() }))
      .filter((a) => a.answer);
    return [...rounds, { ...current, answers: answered }];
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (current) void send(withCurrentRound(), false);
    else if (canStart) void send([], false);
  }

  const working = jobId !== null;
  const roundNumber = rounds.length + 1;
  const isLastRound = roundNumber >= PRD_BRAINSTORM_MAX_ROUNDS;
  const canStart =
    !uploading &&
    (sources.length > 0 || description.trim().length >= PRD_BRAINSTORM_MIN_DESCRIPTION);
  const started = rounds.length > 0 || current !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="max-w-lg">
      {/* Below md the Dialog itself caps to the visible viewport and scrolls, so a second inner
          scroller here would only nest scroll areas under the on-screen keyboard. */}
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 md:max-h-[80vh] md:overflow-y-auto md:pr-1"
      >
        <DialogHeader className="pr-6">
          <DialogTitle>Brainstorm a PRD</DialogTitle>
          <DialogDescription>
            {working
              ? finishing
                ? 'Writing the PRD draft…'
                : 'Thinking of follow-up questions…'
              : current
                ? `Round ${roundNumber} of up to ${PRD_BRAINSTORM_MAX_ROUNDS} — answer what you can, skip the rest.`
                : 'Describe the project in a few sentences, attach any notes or briefs you have, or both. A few follow-up questions will turn it into a first-draft PRD.'}
          </DialogDescription>
        </DialogHeader>

        {working && (
          <div className="flex flex-col gap-2" aria-live="polite">
            <div className="h-1 overflow-hidden rounded-full bg-surface-2">
              <div className="h-full w-1/3 animate-pulse rounded-full bg-accent" />
            </div>
          </div>
        )}

        {!working && !current && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="prd-brainstorm-description">Project description</Label>
            <Textarea
              id="prd-brainstorm-description"
              rows={5}
              autoFocus
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={4000}
              placeholder="e.g. A self-serve portal where our clients can see the status of their open support tickets and approve quotes, instead of emailing the account manager."
            />

            <div className="mt-2 flex flex-col gap-1.5">
              <span className="text-sm">
                Documents <span className="text-muted">(optional)</span>
              </span>
              {sources.length > 0 && (
                <ul className="flex flex-col gap-1">
                  {sources.map((s) => (
                    <li
                      key={s.storagePath}
                      className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm"
                    >
                      <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-muted" />
                      <span className="flex-1 truncate">{s.name}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${s.name}`}
                        onClick={() =>
                          setSources((list) => list.filter((x) => x.storagePath !== s.storagePath))
                        }
                        className="-m-2 p-2 text-muted hover:text-danger"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={SOURCE_FILE_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = '';
                  if (files.length > 0) void handleAttach(files);
                }}
              />
              {sources.length < PRD_SOURCE_MAX_FILES && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  {uploading ? 'Uploading…' : 'Attach documents'}
                </Button>
              )}
              <p className="text-xs text-muted">
                PDF, Markdown, text, CSV or images — up to {PRD_SOURCE_MAX_FILES}.
              </p>
            </div>
          </div>
        )}

        {!working && current && sources.length > 0 && (
          <p className="text-xs text-muted">
            Using {sources.length === 1 ? sources[0]!.name : `${sources.length} attached documents`}.
          </p>
        )}

        {!working && current && (
          <div className="flex flex-col gap-5">
            {current.clarification && (
              <p className="rounded-lg border border-border bg-surface-2 p-3 text-sm">
                {current.clarification}
              </p>
            )}
            {current.questions.map((q) => (
              <BrainstormQuestion
                key={q.id}
                question={q}
                value={answers[q.id] ?? ''}
                onChange={(value) => setAnswers((a) => ({ ...a, [q.id]: value }))}
              />
            ))}
          </div>
        )}

        {!working && formHasContent && (
          <p className="text-xs text-muted">
            The draft replaces the matching fields in the form. Nothing is saved until you save
            the draft.
          </p>
        )}

        {error && <p className="text-sm text-danger">{error}</p>}

        {!working && (
          <DialogFooter className="flex-col-reverse sm:flex-row sm:flex-wrap">
            {started && (
              <Button type="button" variant="ghost" className="sm:mr-auto" onClick={reset}>
                Start over
              </Button>
            )}
            {current ? (
              <>
                {!isLastRound && (
                  <Button
                    type="button"
                    disabled={submitting}
                    onClick={() => void send(withCurrentRound(), true)}
                  >
                    Generate PRD now
                  </Button>
                )}
                <Button type="submit" variant="default" disabled={submitting}>
                  {isLastRound ? 'Generate PRD' : 'Continue'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  disabled={submitting || !canStart}
                  onClick={() => void send([], true)}
                >
                  Skip questions
                </Button>
                <Button type="submit" variant="default" disabled={submitting || !canStart}>
                  Continue
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </form>
    </Dialog>
  );
}

/** One follow-up question: pick a suggested option, or write a different answer. */
function BrainstormQuestion({
  question,
  value,
  onChange,
}: {
  question: PrdBrainstormQuestion;
  value: string;
  onChange: (value: string) => void;
}) {
  const options = question.suggestedOptions;
  const pickedOption = options.some((o) => o.label === value);
  const inputId = `prd-brainstorm-${question.id}`;

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-sm font-medium">{question.prompt}</legend>
      {question.helpText && <p className="-mt-1 text-xs text-muted">{question.helpText}</p>}
      {options.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {options.map((o) => {
            const selected = o.label === value;
            return (
              <button
                key={o.label}
                type="button"
                aria-pressed={selected}
                title={o.description}
                onClick={() => onChange(selected ? '' : o.label)}
                className={cn(
                  'min-h-10 rounded-lg border px-3 py-1.5 text-left text-xs transition-colors outline-none focus-visible:ring-2 focus-visible:ring-focus md:min-h-0',
                  selected
                    ? 'border-accent bg-accent-soft'
                    : 'border-border hover:border-[var(--ink-400)]',
                )}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
      <Input
        id={inputId}
        aria-label={options.length > 0 ? `${question.prompt} — your own answer` : question.prompt}
        value={pickedOption ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={1000}
        placeholder={options.length > 0 ? 'or write your own answer' : 'Your answer'}
      />
    </fieldset>
  );
}
